/**
 * Text Direction Utilities
 *
 * Utilities for detecting text direction and language scripts.
 * Supports bidirectional text (Arabic, Hebrew) and Latin scripts.
 */

/**
 * Check if text contains Arabic characters
 *
 * Unicode ranges:
 * - U+0600–U+06FF: Arabic
 * - U+0750–U+077F: Arabic Supplement
 * - U+08A0–U+08FF: Arabic Extended-A
 * - U+FB50–U+FDFF: Arabic Presentation Forms-A
 * - U+FE70–U+FEFF: Arabic Presentation Forms-B
 *
 * @param text - The text to check
 * @returns true if text contains at least one Arabic character
 */
export function isArabic(text: string): boolean {
  return /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/.test(text);
}

/**
 * Check if text contains Latin characters (A-Z, a-z)
 *
 * @param text - The text to check
 * @returns true if text contains at least one Latin letter
 */
export function hasLatinChars(text: string): boolean {
  return /[A-Za-z]/.test(text);
}

/**
 * Check if text is purely Arabic (no Latin characters)
 *
 * Note: Numbers and punctuation don't count as "mixed" - they're neutral
 * in bidirectional text.
 *
 * @param text - The text to check
 * @returns true if text has Arabic and no Latin characters
 */
export function isPureArabic(text: string): boolean {
  return isArabic(text) && !hasLatinChars(text);
}

/**
 * Determine if text should use RTL direction
 *
 * @param text - The text to check
 * @returns true if text should be rendered RTL
 */
export function shouldUseRTL(text: string): boolean {
  return isPureArabic(text);
}
